import React, { createContext, useState } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../context/StateContext";
import { YupHandlers, useYupErrors, useYupSchemas } from "../../../hooks/useYupValidation";
import { Shipment } from "../../../models/shipments";
import { ShipmentType } from "../../../util/enums";
import { useShipmentContext } from "../hooks/useShipmentContext";

interface ShipmentWithAll extends Shipment {
  all: any;
}

interface ErrorsContextProps extends YupHandlers<ShipmentWithAll> {
  errors: yup.ValidationError[];
  handleValidate: () => boolean;
}

interface ErrorsProviderProvider {
  children: React.ReactChild;
}

const ErrorsContext = createContext<ErrorsContextProps>({} as ErrorsContextProps);

const ErrorsProvider = ({ children }: ErrorsProviderProvider) => {
  const { t: translate } = useTranslation();
  const { isFMVersion } = useAppState();

  const { shipment } = useShipmentContext();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const yupHandlers = useYupErrors<ShipmentWithAll>(errors);
  const { stringRequired, numberRequired, objectRequired } = useYupSchemas(translate);

  const schema = yup.object().shape({
    shipmentType: stringRequired,
    statusShipment: stringRequired,
    containerId: stringRequired,
    eventType: yup.mixed().when("isFmVersion", {
      is: true,
      then: yup.string().required(translate("thisFieldIsRequired")),
    }),
    projectId: numberRequired,
    bookingNo: stringRequired,
    originLocation: yup.mixed().when("basedOnBooking", {
      is: false,
      then: yup.string().required(translate("thisFieldIsRequired")),
    }),
    destinationLocation: yup.mixed().when("basedOnBooking", {
      is: false,
      then: yup.string().required(translate("thisFieldIsRequired")),
    }),
    carrierId: numberRequired,
    consigneeId: numberRequired,
    shipperEntity: objectRequired,
    billOfLanding: stringRequired
      .min(7, translate("billOfLandingMustBeAtLeast7CharactersLong"))
      .max(20, translate("billOfLandingMustBeAtMax20CharactersLong")),
    serviceType: stringRequired,
    incoterm: stringRequired,
    loadType: yup
      .mixed()
      .when("shipmentType", {
        is: (value: ShipmentType) => value !== ShipmentType.RAIL && value,
        then: yup
          .string()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
      })
      .typeError(translate("thisFieldIsRequired")),
  });

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          isFMVersion,
          shipmentType: shipment?.shipmentType,
          statusShipment: shipment?.statusShipment,
          containerId: shipment?.containerId,
          eventType: shipment?.eventType,
          products: shipment?.products,
          warehouseId: shipment?.warehouseId,
          projectId: shipment?.projectId,
          carrierId: shipment?.carrierId,
          consigneeId: shipment?.consigneeId,
          bookingNo: shipment?.bookingNo,
          supplierId: shipment?.supplierId,
          billOfLanding: shipment?.billOfLanding,
          serviceType: shipment?.serviceType,
          incoterm: shipment?.incoterm,
          loadType: shipment?.loadType,
          destinationLocation: shipment?.destinationLocation?.longAddress,
          originLocation: shipment?.originLocation?.longAddress,
          basedOnBooking: !!shipment?.bookingId,
          shipperEntity: shipment?.shipperEntity,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error: any) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  return (
    <ErrorsContext.Provider
      value={{
        errors,
        handleValidate,
        ...yupHandlers,
      }}
    >
      {children}
    </ErrorsContext.Provider>
  );
};

export { ErrorsContext, ErrorsProvider };
